import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../usercomponents/SideBar';
import TopNavBar from '../usercomponents/TopNavBar';
import Footer from '../usercomponents/Footer';
import MockExamQuestionRenderer from '../usercomponents/MockExamQuestionRenderer';

const apiUrl = process.env.REACT_APP_API_URL;

function isAnswerEmpty(answer) {
  if (!answer) return true;
  if (typeof answer.text === 'string') return !answer.text.trim();
  if (answer.selected_option_id) return false;
  if (Array.isArray(answer.selected_option_ids)) return answer.selected_option_ids.length === 0;
  if (answer.value !== undefined && answer.value !== null && answer.value !== '') return false;
  return true;
}

function formatRemaining(ms) {
  if (ms <= 0) return '00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

function MockExamTake() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [remainingMs, setRemainingMs] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const saveTimerRef = useRef(null);

  const getToken = () => JSON.parse(sessionStorage.getItem('studentSession'))?.token;

  const fetchState = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/student/mock-exams/attempts/${attemptId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        setAttempt(response.data.attempt);
        setQuestions(response.data.questions || []);
        if (response.data.attempt.status !== 'in_progress') {
          navigate(`/mock-exam-result/${attemptId}`);
          return;
        }
        if (response.data.attempt.expires_at) {
          setRemainingMs(new Date(response.data.attempt.expires_at).getTime() - Date.now());
        }
      } else {
        setError(response.data.message || 'Failed to load attempt.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading attempt.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId]);

  useEffect(() => { fetchState(); }, [fetchState]);

  // Server-authoritative countdown — purely cosmetic client-side ticking,
  // re-verified by the server on every save/submit call.
  useEffect(() => {
    if (remainingMs === null) return;
    const interval = setInterval(() => {
      setRemainingMs((prev) => {
        const next = prev - 1000;
        if (next <= 0) {
          clearInterval(interval);
          handleSubmit(true);
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingMs === null]);

  const currentQuestion = questions[currentIndex];

  const saveAnswer = useCallback(async (attemptQuestionId, answer, markedForReview) => {
    try {
      const token = getToken();
      const response = await axios.put(
        `${apiUrl}/api/student/mock-exams/attempts/${attemptId}/questions/${attemptQuestionId}`,
        { answer, markedForReview },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 409 || (response.data && response.data.success === false && response.data.message?.includes('already been submitted'))) {
        navigate(`/mock-exam-result/${attemptId}`);
      }
    } catch (err) {
      if (err.response?.status === 409) {
        navigate(`/mock-exam-result/${attemptId}`);
      }
    }
  }, [attemptId, navigate]);

  const handleAnswerChange = (newAnswer) => {
    setQuestions((prev) => prev.map((q, i) => (i === currentIndex ? { ...q, student_answer_json: newAnswer, is_answered: !isAnswerEmpty(newAnswer) } : q)));
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveAnswer(currentQuestion.id, newAnswer, currentQuestion.is_marked_for_review);
    }, 500);
  };

  const toggleMarkForReview = () => {
    const next = !currentQuestion.is_marked_for_review;
    setQuestions((prev) => prev.map((q, i) => (i === currentIndex ? { ...q, is_marked_for_review: next } : q)));
    saveAnswer(currentQuestion.id, currentQuestion.student_answer_json, next);
  };

  const clearAnswer = () => {
    setQuestions((prev) => prev.map((q, i) => (i === currentIndex ? { ...q, student_answer_json: null, is_answered: false } : q)));
    saveAnswer(currentQuestion.id, null, currentQuestion.is_marked_for_review);
  };

  const goTo = (idx) => {
    clearTimeout(saveTimerRef.current);
    if (currentQuestion) saveAnswer(currentQuestion.id, currentQuestion.student_answer_json, currentQuestion.is_marked_for_review);
    setCurrentIndex(idx);
  };

  const answeredCount = questions.filter((q) => q.is_answered).length;
  const markedCount = questions.filter((q) => q.is_marked_for_review).length;
  const unansweredCount = questions.length - answeredCount;

  const handleSubmit = async (auto = false) => {
    setSubmitting(true);
    setError('');
    try {
      clearTimeout(saveTimerRef.current);
      if (currentQuestion && !auto) {
        await saveAnswer(currentQuestion.id, currentQuestion.student_answer_json, currentQuestion.is_marked_for_review);
      }
      const token = getToken();
      const response = await axios.post(`${apiUrl}/api/student/mock-exams/attempts/${attemptId}/submit`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        navigate(`/mock-exam-result/${attemptId}`);
      } else {
        setError(response.data.message || 'Failed to submit.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error submitting exam.');
    } finally {
      setSubmitting(false);
      setShowConfirm(false);
    }
  };

  if (loading) {
    return (
      <>
        <SideBar />
        <div className="dashboard-main-wrapper"><TopNavBar /><div className="dashboard-body text-center py-40"><div className="spinner-border text-main-600" /></div><Footer /></div>
      </>
    );
  }

  if (error && !attempt) {
    return (
      <>
        <SideBar />
        <div className="dashboard-main-wrapper"><TopNavBar /><div className="dashboard-body"><div className="alert alert-danger">{error}</div></div><Footer /></div>
      </>
    );
  }

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body px-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-8 mb-16">
            <div>
              <div className="fw-bold text-16">{attempt.exam_title}</div>
              <div className="text-13 text-gray-500">Question {currentIndex + 1} of {questions.length}</div>
            </div>
            {remainingMs !== null && (
              <div className={`fw-bold text-20 ${remainingMs < 60000 ? 'text-danger' : 'text-main-600'}`}>
                <i className="ph ph-timer me-1" /> {formatRemaining(remainingMs)}
                {remainingMs < 60000 && <div className="text-13 text-danger">Time is almost up!</div>}
              </div>
            )}
          </div>

          {error && <div className="alert alert-danger mb-16">{error}</div>}

          <div className="row g-16">
            <div className="col-lg-9">
              {currentQuestion && (
                <div className="card shadow-sm border-0">
                  <div className="card-body p-24">
                    <div className="d-flex justify-content-between mb-12">
                      <span className="text-13 text-gray-400 text-capitalize">{currentQuestion.question_type.replace('_', ' ')} · {currentQuestion.marks_snapshot} mark(s)</span>
                      <button type="button" className={`btn btn-sm py-4 px-12 rounded-pill ${currentQuestion.is_marked_for_review ? 'btn-warning' : 'btn-outline-main'}`} onClick={toggleMarkForReview}>
                        <i className="ph ph-flag me-1" /> {currentQuestion.is_marked_for_review ? 'Marked' : 'Mark for Review'}
                      </button>
                    </div>
                    <div className="fw-semibold text-16 mb-20">{currentQuestion.question_text_snapshot}</div>
                    <MockExamQuestionRenderer question={currentQuestion} answer={currentQuestion.student_answer_json} onChange={handleAnswerChange} />

                    <div className="d-flex justify-content-between mt-24 flex-wrap gap-8">
                      <div className="d-flex gap-8">
                        <button type="button" className="btn btn-outline-main rounded-pill py-9 px-20" disabled={currentIndex === 0} onClick={() => goTo(currentIndex - 1)}>Previous</button>
                        <button type="button" className="btn btn-outline-main rounded-pill py-9 px-20" onClick={clearAnswer}>Clear Answer</button>
                      </div>
                      <div className="d-flex gap-8">
                        {currentIndex < questions.length - 1 ? (
                          <button type="button" className="btn btn-main rounded-pill py-9 px-20" onClick={() => goTo(currentIndex + 1)}>Save & Next</button>
                        ) : (
                          <button type="button" className="btn btn-main rounded-pill py-9 px-20" onClick={() => setShowConfirm(true)}>Submit Exam</button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="col-lg-3">
              <div className="card shadow-sm border-0">
                <div className="card-body p-16">
                  <div className="d-flex justify-content-between text-13 mb-12">
                    <span className="text-success">Answered: {answeredCount}</span>
                    <span className="text-gray-500">Unanswered: {unansweredCount}</span>
                  </div>
                  <div className="text-13 text-warning mb-12">Marked for review: {markedCount}</div>
                  <div className="d-flex flex-wrap gap-8 mb-16">
                    {questions.map((q, idx) => {
                      let bg = '#e5e7eb';
                      if (q.is_marked_for_review) bg = '#f5a623';
                      else if (q.is_answered) bg = '#0f9d58';
                      return (
                        <button
                          key={q.id} type="button" onClick={() => goTo(idx)}
                          className="d-flex align-items-center justify-content-center"
                          style={{
                            width: 34, height: 34, borderRadius: 8, border: idx === currentIndex ? '2px solid #030917' : 'none',
                            background: bg, color: '#fff', fontSize: 13, fontWeight: 600,
                          }}
                        >
                          {idx + 1}
                        </button>
                      );
                    })}
                  </div>
                  <button type="button" className="btn btn-main rounded-pill py-9 w-100" onClick={() => setShowConfirm(true)}>Submit Exam</button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {showConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(3,9,23,0.55)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div className="card border-0" style={{ maxWidth: 420, width: '100%' }}>
            <div className="card-body p-24">
              <h5 className="fw-bold mb-16">Submit Exam?</h5>
              <div className="d-flex flex-column gap-8 mb-20">
                <div className="d-flex justify-content-between"><span>Total Questions</span><strong>{questions.length}</strong></div>
                <div className="d-flex justify-content-between"><span>Answered</span><strong className="text-success">{answeredCount}</strong></div>
                <div className="d-flex justify-content-between"><span>Unanswered</span><strong className="text-danger">{unansweredCount}</strong></div>
                <div className="d-flex justify-content-between"><span>Marked for Review</span><strong className="text-warning">{markedCount}</strong></div>
              </div>
              <p className="text-13 text-gray-500 mb-20">Once submitted, you cannot change your answers.</p>
              <div className="d-flex justify-content-end gap-8">
                <button type="button" className="btn btn-outline-main rounded-pill py-9 px-20" onClick={() => setShowConfirm(false)} disabled={submitting}>Cancel</button>
                <button type="button" className="btn btn-main rounded-pill py-9 px-20" onClick={() => handleSubmit(false)} disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Confirm Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default MockExamTake;
